import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import { Box, Grid, Stack, Typography } from "@mui/material";

import { NavLink } from "@ui/index";
import { Text } from "@ui/typography";

import { useSubitems } from "@hooks/useSubitems";
import { useWhitelistingPausedRedirect,WHITELISTING_PAUSED_HINT } from "@hooks/useWhitelistingPausedRedirect";
import { useWhitelistingSuspended } from "@hooks/useWhitelistingSuspended";

import { QuestionFlowDialog } from "@components/QuestionFlowDialog";
import { megaMenuGroups } from "@layouts/constants";
import { getSubitems } from "@layouts/helpers";
import { DropdownNavItem } from "@layouts/types";

import { GeneralLogo, LicenseTestShield } from "@/assets/svg";
import { StyledDivider } from "@/components/components";
import { AccountType,useAccountType } from "@/contexts/AccountTypeContext";
import { useAuth } from "@/hooks/auth";
import { useActionRequiredCount } from "@/hooks/useActionRequiredCount";
import { usePresenceHeartbeat } from "@/hooks/usePresenceHeartbeat";
import AccountMenu from "@/layouts/components/AccountMenu";
import AccountRecoveredGlobalBanner from "@/layouts/components/AccountRecoveredGlobalBanner";
import BetaGlobalBanner from "@/layouts/components/BetaGlobalBanner";
import CancelledGlobalBanner from "@/layouts/components/CancelledGlobalBanner";
import {
  SecondHeaderContainer,
  SubItem,
} from "@/layouts/components/components";
import DisconnectChannelsReminderBanner from "@/layouts/components/DisconnectChannelsReminderBanner";
import DowngradeSuccessBanner from "@/layouts/components/DowngradeSuccessBanner";
import GlobalIncidentBanner from "@/layouts/components/GlobalIncidentBanner";
import GlobalUpdatePaymentModal from "@/layouts/components/GlobalUpdatePaymentModal";
import GracePeriodGuard from "@/layouts/components/GracePeriodGuard";
import PastDueGlobalBanner from "@/layouts/components/PastDueGlobalBanner";
import PendingDowngradeGlobalModal from "@/layouts/components/PendingDowngradeGlobalModal";
import PendingEmailGlobalBanner from "@/layouts/components/PendingEmailGlobalBanner";
import WhitelistingSuspendedGlobalBanner from "@/layouts/components/WhitelistingSuspendedGlobalBanner";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { toggleCart } from "@/store/reducers/cart";
import { openConsentPreferences } from "@/utils/consent";
import { ERoutes } from "@/utils/constants";

import MegaMenu from "./MegaMenu";

// Tracks whether the header-content fade-in has already played this page load,
// so it doesn't replay on SPA route changes / remounts. Resets on full reload.
let headerContentHasFadedIn = false;

type TSecondHeader = {
  handleOpenDialog: (isLogin: boolean) => void;
};

const SecondHeader: FC<TSecondHeader> = ({ handleOpenDialog }) => {
  const { t } = useTranslation("navigation");
  const { isLoggedIn, isLoading } = useAuth();
  const { accountType } = useAccountType();
  const isCreator = accountType === AccountType.MUSIC_CREATOR;
  const actionRequiredCount = useActionRequiredCount();
  usePresenceHeartbeat(); // bump users.lastSeenAt while active (After-inactivity email policy)
  const { subitems, itemRefs, underlineRef } = useSubitems(getSubitems);

  // Thin gold separators between nav items. Visual rhythm; doesn't add
  // chrome around the items themselves.
  const navStackSx = {
    height: "72px",
    "& > div": { position: "relative" },
    "& > div:not(:last-child)::after": {
      content: '""',
      position: "absolute",
      right: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: "1px",
      height: "18px",
      background: "rgba(235, 194, 142, 0.2)",
      pointerEvents: "none",
    },
  };
  const [openQuestionDialog, setOpenQuestionDialog] = useState(false);
  const dispatch = useAppDispatch();
  const cartItems = useAppSelector((state) => state.cart.items);
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const whitelistingSuspended = useWhitelistingSuspended();
  const goFixWhitelisting = useWhitelistingPausedRedirect();
  const favoritesCount = useAppSelector((state) => state.favorites.favorites.length);
  const hasFavorites = favoritesCount > 0;

  // Hide on scroll down, show on scroll up — but stay pinned (no
  // fade) on /account/* pages with sidebars so the fading header
  // doesn't collide with the sticky sidebar. /account/claims uses
  // `noSidebar`, so it gets the fade like every other page.
  const { pathname } = useLocation();
  const disableFade = pathname.startsWith("/account") && !pathname.startsWith(ERoutes.AccountVideos);
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);

  // Header-content fade-in: play ONCE per full page load. The module flag
  // survives remounts; the timeout disables it after it plays so SPA navigations
  // (incl. leaving /account, which flips disableFade) don't replay it.
  const [fadeHeaderContent, setFadeHeaderContent] = useState(() => !headerContentHasFadedIn);
  useEffect(() => {
    headerContentHasFadedIn = true;
    if (!fadeHeaderContent) return;
    const id = setTimeout(() => setFadeHeaderContent(false), 800);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (disableFade) {
      setHeaderVisible(true);
      return;
    }
    lastScrollY.current = window.scrollY;
    setHeaderVisible(true);

    // Browser scroll restoration and programmatic scrolls during first
    // paint both fire `scroll` events. We stabilize for 600ms after mount:
    // during that window every scroll event is treated as noise (baseline
    // is re-synced, no direction decision), so the header never hides
    // until the user genuinely drags/wheels after the page has settled.
    let stabilized = false;
    const stabilizeTimer = window.setTimeout(() => {
      stabilized = true;
      lastScrollY.current = window.scrollY;
    }, 600);

    // Ignore jitter smaller than this (px) so small bounces or layout
    // shifts don't toggle the header.
    const DIRECTION_THRESHOLD = 5;

    const handleScroll = () => {
      if (!stabilized) {
        // Keep the baseline in sync with the settling page — any scroll
        // here is the browser's doing, not the user's.
        lastScrollY.current = window.scrollY;
        return;
      }
      // Keep the header pinned while the user is typing in a field (e.g. the
      // catalog search) so it never fades out from under an active search —
      // focusing an input on mobile opens the keyboard and fires scroll events.
      const ae = document.activeElement;
      if (ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA")) {
        setHeaderVisible(true);
        lastScrollY.current = window.scrollY;
        return;
      }
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;
      if (Math.abs(delta) < DIRECTION_THRESHOLD) return;
      setHeaderVisible(delta < 0);
      lastScrollY.current = currentY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.clearTimeout(stabilizeTimer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [disableFade]);

  // Mega menu state
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
  const lastActiveMegaMenu = useRef<string | null>(null);
  const navItemRefs = useRef<Record<string, HTMLElement | null>>({});

  const handleMegaMenuToggle = useCallback(
    (key: string) => {
      if (activeMegaMenu === key) {
        setActiveMegaMenu(null);
        lastActiveMegaMenu.current = null;
        return;
      }
      setActiveMegaMenu(key);
      lastActiveMegaMenu.current = key;
    },
    [activeMegaMenu],
  );

  // User clicked outside — close and forget
  const closeMegaMenu = useCallback(() => {
    setActiveMegaMenu(null);
    lastActiveMegaMenu.current = null;
  }, []);

  // Resize to mobile — close but remember
  const hideMegaMenu = useCallback(() => {
    setActiveMegaMenu(null);
  }, []);

  // Restore mega menu when resizing back to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024 && !activeMegaMenu && lastActiveMegaMenu.current) {
        setActiveMegaMenu(lastActiveMegaMenu.current);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [activeMegaMenu]);

  const handleCookieClock = () => {
    openConsentPreferences();
  };

  const renderSubitems = ({ route, key, icon, target }: DropdownNavItem, i: number) => (
    <SubItem
      key={route}
      ref={(el) => (itemRefs.current[i] = el as HTMLDivElement)}
      sx={{
        "&& .active p":
          ((route !== ERoutes.Test && location.search.includes(ERoutes.Test)) || route !== ERoutes.Test) &&
          key !== "Cookies"
            ? {
                color: "#fff",
                svg: { fill: "#fff" },
              }
            : {},
        "&& a": {
          textDecoration: "none",
          textUnderlineOffset: "unset",
        },
      }}
    >
      <NavLink
        noLineAnimation={true}
        to={route}
        target={target}
        end
        className={
          route === ERoutes.Catalog && location.pathname.includes(ERoutes.CatalogLandingFreeBackground) ? "active" : ""
        }
        onClick={(e) => {
          if (key === "Cookies") {
            e.preventDefault();
            handleCookieClock();
          }
        }}
      >
        {!location.pathname.includes(ERoutes.Deals) && icon && icon}

        <Text isFullWidth size={1} sx={{ "&&": { color: "#F5DFC4" } }}>
          {t(key)}
        </Text>
        {location.pathname.includes(ERoutes.Deals) && icon && icon}
      </NavLink>
    </SubItem>
  );

  return (
    <>
      <SecondHeaderContainer onClick={closeMegaMenu} style={{
          // On /account/* the header drops out of sticky / fade and flows
          // normally — scrolling the page scrolls the header away too.
          position: disableFade ? "static" : undefined,
          transform: headerVisible ? "translateY(0)" : "translateY(-20px)",
          opacity: headerVisible ? 1 : 0,
          pointerEvents: headerVisible ? undefined : "none",
          transition: disableFade ? "none" : "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease",
          // Brown nav bg only covers the first 72px so the LoggedInBanner
          // sitting below it has a transparent parent — its
          // backdrop-filter can then blur the page content underneath.
          background: "linear-gradient(to bottom, #59412e 0, #59412e 72px, transparent 72px)",
          backgroundColor: "transparent",
        }}>
        {/* Dev switchers moved out of the header into a fixed left-side
            column rendered by `DevSwitcherRail` in SecondRootLayout. */}
        <Grid
          container
          justifyContent={"space-between"}
          direction="row"
          alignItems="center"
          margin="0 auto"
          height="100%"
          width="100%"
          maxWidth="1280px"
          paddingX={{ xs: "16px", md: "40px" }}
          minHeight="72px"
          // Make this the positioning context for the absolute-centered nav.
          sx={{
            position: "relative",
            // Smooth one-time fade-in of the header content on page load. Opacity
            // only — this Grid (logo / nav / right-side actions) has no
            // backdrop-filter, so no blur-snap (the glassy LoggedInBanner sits
            // outside it). A keyframe (not a JS opacity:0 initial) keeps the
            // resting state opacity:1 so prerender/crawlers see it; `both` holds
            // the 0% frame before it runs so there's no flash. Skipped on /account
            // where the header is intentionally static.
            "@keyframes headerContentFadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
            animation: fadeHeaderContent && !disableFade ? "headerContentFadeIn 0.6s ease both" : "none",
          }}
        >
          {/* Logo */}
          <Grid item xs={2.5} lg="auto" md="auto" sm="auto" sx={{ transform: "translateY(-2px)" }}>
            <Link to={ERoutes.Home}>
              <Stack height="32px">
                <GeneralLogo style={{ height: "100%", width: "100%" }} />
              </Stack>
            </Link>
          </Grid>

          {/* Nav Items — Raleway style. Absolutely centered to the
              container so the gap to the logo on the left and to the
              heart icon on the right stay symmetric regardless of how
              wide the right-side cluster grows. */}
          <Grid
            item
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              maxWidth: "70%",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="center" sx={navStackSx}>
              {/* Music — direct link. Anchor uses plain inline rendering
                  (not inline-flex) so trailing-letter spacing matches the
                  text-only nav items like Deals / Contact. The icon sits
                  inline via vertical-align + margin-right. */}
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  px: "24px",
                  "& a": {
                    fontFamily: '"Raleway", sans-serif',
                    fontSize: "16px",
                    fontWeight: 400,
                    color: "#fff",
                    letterSpacing: "1px",
                    textDecoration: "none",
                    transition: "color 0.3s ease",
                    "&:hover": { color: "#FFDCA8" },
                  },
                }}
              >
                <NavLink noLineAnimation={true} to={ERoutes.Catalog} end>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 640 640"
                    style={{ verticalAlign: "middle", marginRight: "8px", flexShrink: 0 }}
                    aria-hidden="true"
                  >
                    <path
                      style={{ fill: "currentColor" }}
                      d="M532 71C539.6 77.1 544 86.3 544 96L544 400C544 444.2 501 480 448 480C395 480 352 444.2 352 400C352 355.8 395 320 448 320C459.2 320 470 321.6 480 324.6L480 207.9L256 257.7L256 464C256 508.2 213 544 160 544C107 544 64 508.2 64 464C64 419.8 107 384 160 384C171.2 384 182 385.6 192 388.6L192 160C192 145 202.4 132 217.1 128.8L505.1 64.8C514.6 62.7 524.5 65 532.1 71.1z"
                    />
                  </svg>
                  {t("music")}
                </NavLink>
              </Box>

              {/* Licensing mega menu — rendered before Deals */}
              {megaMenuGroups.filter((g) => g.key === "licensing").map((group) => (
                <Box
                  key={group.key}
                  ref={(el: HTMLElement | null) => {
                    navItemRefs.current[group.key] = el;
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMegaMenuToggle(group.key);
                  }}
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    position: "relative",
                    px: "24px",
                    "&:hover .mega-nav-label": {
                      color: "#FFDCA8",
                    },
                  }}
                >
                  <Typography
                    className="mega-nav-label"
                    sx={{
                      fontFamily: '"Raleway", sans-serif',
                      fontSize: "16px",
                      fontWeight: 400,
                      color: activeMegaMenu === group.key ? "#FFDCA8" : "#fff",
                      letterSpacing: "1px",
                      transition: "color 0.3s ease",
                      userSelect: "none",
                    }}
                  >
                    {t(group.translationKey)}
                  </Typography>
                </Box>
              ))}

              {/* Deals used to sit here as a direct link, between Licensing and
                  Contact. Removed from the desktop main menu 2026-07-26 (user);
                  the page itself and its footer link are untouched. */}

              {/* Contact mega menu */}
              {megaMenuGroups.filter((g) => g.key === "contact").map((group) => (
                <Box
                  key={group.key}
                  ref={(el: HTMLElement | null) => {
                    navItemRefs.current[group.key] = el;
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMegaMenuToggle(group.key);
                  }}
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    position: "relative",
                    px: "24px",
                    "&:hover .mega-nav-label": {
                      color: "#FFDCA8",
                    },
                  }}
                >
                  <Typography
                    className="mega-nav-label"
                    sx={{
                      fontFamily: '"Raleway", sans-serif',
                      fontSize: "16px",
                      fontWeight: 400,
                      color: activeMegaMenu === group.key ? "#FFDCA8" : "#fff",
                      letterSpacing: "1px",
                      transition: "color 0.3s ease",
                      userSelect: "none",
                    }}
                  >
                    {t(group.translationKey)}
                  </Typography>
                </Box>
              ))}

              {/* Pricing — direct link for guests and free users (both can
                  still upgrade). Subscribers + Music Creators manage their
                  plan via /account/subscription, so the page is noise for
                  them and is hidden. */}
              {(!isLoggedIn || accountType === AccountType.FREE) && !isLoading && (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    px: "24px",
                    "& a": {
                      fontFamily: '"Raleway", sans-serif',
                      fontSize: "16px",
                      fontWeight: 400,
                      color: "#fff",
                      letterSpacing: "1px",
                      textDecoration: "none",
                      transition: "color 0.3s ease",
                      "&:hover": { color: "#FFDCA8" },
                    },
                  }}
                >
                  <NavLink
                    noLineAnimation={true}
                    to={ERoutes.Pricing}
                    className={location.pathname.includes(ERoutes.Pricing) ? "active" : ""}
                    end
                  >
                    Pricing
                  </NavLink>
                </Box>
              )}

            </Stack>
          </Grid>

          {/* Auth / User section */}
          <Grid item md="auto">
            <Stack width="100%" alignItems="end" justifyContent="center">
              {/* Auth not resolved yet → render nothing (not the guest sign-in
                  CTAs) so we don't flash signed-out controls before the user
                  loads. */}
              {isLoading ? null : isLoggedIn ? (
                <Stack
                  direction="row"
                  width="100%"
                  spacing="12px"
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ minWidth: "fit-content" }}
                >
                  {/* Favorites — heart SVG fills white when the user has any
                      favorites; otherwise it stays as an outline that matches
                      the cart icon styling. */}
                  <Box
                    component={Link}
                    to={ERoutes.Favorites}
                    sx={{
                      width: "40px",
                      height: "40px",
                      border: "1.5px solid rgba(255,255,255,0.5)",
                      backgroundColor: "transparent",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      position: "relative",
                      transition: "all 0.3s ease",
                      color: "rgba(255,255,255,0.8)",
                      textDecoration: "none",
                      "&:hover": {
                        borderColor: "rgba(255,255,255,0.5)",
                        backgroundColor: "rgba(255,255,255,0.12)",
                        color: "#fff",
                      },
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill={hasFavorites ? "#ffffff" : "none"}
                      stroke={hasFavorites ? "#ffffff" : "currentColor"}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                    </svg>
                    {/* Favorites count badge intentionally removed — the
                        heart icon alone communicates "you have favorites";
                        the dropdown row still shows the exact count for
                        users who want the number. */}
                  </Box>

                  {/* Cart icon */}
                  <Box
                    onClick={() => { if (whitelistingSuspended) return goFixWhitelisting(); dispatch(toggleCart()); }}
                    title={whitelistingSuspended ? WHITELISTING_PAUSED_HINT : undefined}
                    sx={{
                      width: "40px",
                      height: "40px",
                      border: "1.5px solid rgba(255,255,255,0.5)",
                      backgroundColor: "transparent",
                      borderRadius: "50%",
                      // Hidden for Music Creators — they license via their program, not the cart (A1).
                      display: isCreator ? "none" : "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      position: "relative",
                      transition: "all 0.3s ease",
                      // Blue cart icon when the cart has at least one item.
                      color: cartCount > 0 ? "#9cd3fd" : "rgba(255,255,255,0.8)",
                      "&:hover": {
                        borderColor: "rgba(255,255,255,0.5)",
                        backgroundColor: "rgba(255,255,255,0.12)",
                        color: cartCount > 0 ? "#9cd3fd" : "#fff",
                      },
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ transform: "translate(0px, -1px)" }}
                    >
                      <circle cx="9" cy="20.5" r="1.3" fill="none" /><circle cx="17.4" cy="20.5" r="1.3" fill="none" /><path d="M2 3h3l2.4 12.1a1.7 1.7 0 0 0 1.7 1.4h8.5a1.7 1.7 0 0 0 1.7-1.4L21 7.5H6" />
                    </svg>
                    {cartCount > 0 && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "-4px",
                          right: "-6px",
                          minWidth: "18px",
                          height: "18px",
                          padding: "0 5px",
                          borderRadius: "9px",
                          background: "#ffffff",
                          color: "#3c2d23",
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                          fontSize: "10px",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: "18px",
                        }}
                      >
                        <span style={{ display: "inline-block", transform: "translateY(-1px)" }}>
                          {cartCount > 99 ? "99+" : cartCount}
                        </span>
                      </Box>
                    )}
                  </Box>

                  {/* Claims pill — same 40px height as the surrounding
                      icon circles, pill-shaped to fit the label.
                      When there are pending claims, the pill flips to the
                      blue accent treatment (matches the count badge),
                      drawing the eye to the action-required state. The
                      action-required count rides on the top-right corner
                      like the favorites/cart badges. */}
                  <Box
                    component={Link}
                    to={ERoutes.AccountVideos}
                    sx={{
                      position: "relative",
                      height: "40px",
                      padding: "0 16px",
                      borderRadius: "999px",
                      border: actionRequiredCount > 0 ? "1.5px solid #9cd3fd" : "1.5px solid rgba(255,255,255,0.5)",
                      backgroundColor: actionRequiredCount > 0 ? "#9cd3fd" : "transparent",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: actionRequiredCount > 0 ? "#193e5b" : "rgba(255,255,255,0.85)",
                      textDecoration: "none",
                      fontFamily: '"Raleway", sans-serif',
                      fontSize: "0.8125rem",
                      fontWeight: actionRequiredCount > 0 ? 600 : 500,
                      letterSpacing: "0.3px",
                      whiteSpace: "nowrap",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: actionRequiredCount > 0 ? "#9cd3fd" : "rgba(255,255,255,0.5)",
                        backgroundColor: actionRequiredCount > 0 ? "#b6dffd" : "rgba(255,255,255,0.12)",
                        color: actionRequiredCount > 0 ? "#193e5b" : "#fff",
                      },
                    }}
                  >
                    Claims
                    {actionRequiredCount > 0 && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "-4px",
                          right: "-6px",
                          minWidth: "18px",
                          height: "18px",
                          padding: "0 5px",
                          borderRadius: "9px",
                          // Gold to match the cart count badge — both are
                          // "user has something to do" indicators, same
                          // visual weight.
                          background: "#ffffff",
                          color: "#3c2d23",
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                          fontSize: "10px",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: "18px",
                        }}
                      >
                        <span style={{ display: "inline-block", transform: "translateY(-1px)" }}>
                          {actionRequiredCount}
                        </span>
                      </Box>
                    )}
                  </Box>

                  <AccountMenu />
                </Stack>
              ) : (
                <Stack direction="row" gap="10px" alignItems="center">
                  {/* Cart icon for non-logged-in users */}
                  <Box
                    onClick={() => { if (whitelistingSuspended) return goFixWhitelisting(); dispatch(toggleCart()); }}
                    title={whitelistingSuspended ? WHITELISTING_PAUSED_HINT : undefined}
                    sx={{
                      width: "40px",
                      height: "40px",
                      border: "1.5px solid rgba(255,255,255,0.5)",
                      backgroundColor: "transparent",
                      borderRadius: "50%",
                      // Hidden for Music Creators — they license via their program, not the cart (A1).
                      display: isCreator ? "none" : "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      position: "relative",
                      transition: "all 0.3s ease",
                      // Blue cart icon when the cart has at least one item.
                      color: cartCount > 0 ? "#9cd3fd" : "rgba(255,255,255,0.8)",
                      marginRight: "4px",
                      "&:hover": {
                        borderColor: "rgba(255,255,255,0.5)",
                        backgroundColor: "rgba(255,255,255,0.12)",
                        color: cartCount > 0 ? "#9cd3fd" : "#fff",
                      },
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: "translate(0px, -1px)" }}>
                      <circle cx="9" cy="20.5" r="1.3" fill="none" /><circle cx="17.4" cy="20.5" r="1.3" fill="none" /><path d="M2 3h3l2.4 12.1a1.7 1.7 0 0 0 1.7 1.4h8.5a1.7 1.7 0 0 0 1.7-1.4L21 7.5H6" />
                    </svg>
                    {cartCount > 0 && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "-4px",
                          right: "-6px",
                          minWidth: "18px",
                          height: "18px",
                          padding: "0 5px",
                          borderRadius: "9px",
                          background: "#ffffff",
                          color: "#3c2d23",
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
                          fontSize: "10px",
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: "18px",
                        }}
                      >
                        <span style={{ display: "inline-block", transform: "translateY(-1px)" }}>
                          {cartCount > 99 ? "99+" : cartCount}
                        </span>
                      </Box>
                    )}
                  </Box>
                  <Box
                    component="button"
                    onClick={() => handleOpenDialog(true)}
                    sx={{
                      background: "transparent",
                      border: "1px solid #BA905C",
                      color: "#E0D2C1",
                      fontFamily: "Raleway, sans-serif",
                      fontSize: "14px",
                      fontWeight: 500,
                      padding: "10px 26px",
                      borderRadius: "50px",
                      cursor: "pointer",
                      transition: "all 0.25s ease",
                      letterSpacing: "0.3px",
                      "&:hover": {
                        borderColor: "#FFDCA8",
                        color: "#FFDCA8",
                      },
                    }}
                  >
                    {t("logIn")}
                  </Box>
                  <Box
                    component="button"
                    onClick={() => handleOpenDialog(false)}
                    sx={{
                      background: "linear-gradient(160deg, #8bc5f0 0%, #92ccfa 30%, #a8d8fd 55%, #9cd3fd 75%, #8bc5f0 100%)",
                      border: "none",
                      color: "#1a2a3a",
                      fontFamily: "Raleway, sans-serif",
                      fontSize: "14px",
                      fontWeight: 600,
                      padding: "10px 26px",
                      borderRadius: "50px",
                      cursor: "pointer",
                      transition: "filter 0.25s ease",
                      letterSpacing: "0.3px",
                      "&:hover": {
                        filter: "brightness(1.1)",
                      },
                    }}
                  >
                    {t("signUp")}
                  </Box>
                </Stack>
              )}
            </Stack>
          </Grid>
        </Grid>

        {/* Gold separator */}
        <StyledDivider $isHeader sx={{ "&&": { position: "relative", transform: "translateY(-1px)" } }} />

        {/* Mega Menu */}
        <MegaMenu activeGroupKey={activeMegaMenu} onClose={closeMegaMenu} onHide={hideMegaMenu} navItemRefs={navItemRefs} />

        {/* Subtab bar — hidden */}
        {false && subitems && subitems.length ? (
          <Box sx={{ background: "rgb(127 99 78)", position: "absolute", zIndex: 5, width: "100%" }}>
            <Box
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              width={"100%"}
              maxWidth="1280px"
              margin={"0 auto"}
              paddingX={{ xs: "16px", md: "40px" }}
            >
              <Box display={"flex"} gap={"8px"} alignItems="center" py="8px">
                {subitems.map(renderSubitems)}
                <Box ref={underlineRef} className="nav-underline" sx={{ display: "none" }}></Box>
              </Box>
              <Box
                display="flex"
                gap="8px"
                alignItems="center"
                onClick={() => setOpenQuestionDialog(true)}
                sx={{
                  cursor: "pointer",
                  padding: "8px 16px",
                  borderRadius: "20px",
                  border: "none",
                  color: "rgba(255, 255, 255, 0.5)",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    color: "#FFDCA8",
                  },
                  "& svg": {
                    width: 16,
                    height: 18,
                    stroke: "currentColor",
                  },
                }}
              >
                <LicenseTestShield />
                <Typography
                  sx={{
                    fontFamily: "Raleway",
                    fontSize: "13px",
                    fontWeight: 400,
                    color: "inherit",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("freeUseTestHeader")}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : null}
        {/* Site-wide pending email banner lives INSIDE the fixed
            SecondHeaderContainer so it doesn't disappear behind the
            header. Self-hides on /account (the in-page banner covers
            that) and when no pendingEmail / not logged in / dismissed. */}
        <GlobalIncidentBanner />
        <PendingEmailGlobalBanner />
        <BetaGlobalBanner />
        <PastDueGlobalBanner />
        <WhitelistingSuspendedGlobalBanner />
        <CancelledGlobalBanner />
        <DisconnectChannelsReminderBanner />
        <DowngradeSuccessBanner />
        <AccountRecoveredGlobalBanner />
      </SecondHeaderContainer>
      <PendingDowngradeGlobalModal />
      <GlobalUpdatePaymentModal />
      <GracePeriodGuard />
      <QuestionFlowDialog open={openQuestionDialog} onClose={() => setOpenQuestionDialog(false)} />
    </>
  );
};

export default SecondHeader;
